import React, { useMemo } from 'react';
import { Icon } from '../../misc/Icon/Icon';
import { prepareProps } from '../../utils/propUtils';
import { PropsWithoutChildren } from '../../utils/utilityTypes';
import {
  ModalWithBackdrop,
  ModalWithBackdropProps
} from '../ModalWithBackdrop/ModalWithBackdrop';
import styles from './SelectModal.module.css';

export type SelectModalProps<T> =
  PropsWithoutChildren<ModalWithBackdropProps> & {
    label: string;
    items: Array<T>;
    renderContentOfItem: (item: T) => JSX.Element;
    getKeyOfItem: (item: T) => string;
    onCloseButtonClick: () => void;
    onItemSelected: (item: T) => void;
  };

export const SelectModal = React.memo(<T,>(props: SelectModalProps<T>) => {
  const {
    label,
    items,
    renderContentOfItem,
    getKeyOfItem,
    onCloseButtonClick,
    onItemSelected,
    ...remainingProps
  } = prepareProps({ props, ignoreChildren: true });

  const renderedItems: JSX.Element = useMemo(() => {
    return (
      <>
        {items.map((item) => (
          <li
            key={getKeyOfItem(item)}
            className={styles.listItem}
            data-testid="SelectModal.ListItem"
            // it's okay here because it won't really be rerendered
            // eslint-disable-next-line react/jsx-no-bind
            onClick={() => onItemSelected(item)}
          >
            {renderContentOfItem(item)}
          </li>
        ))}
      </>
    );
  }, [items, getKeyOfItem, renderContentOfItem, onItemSelected]);

  return (
    <ModalWithBackdrop {...remainingProps}>
      <div className={styles.modal}>
        <header className={styles.header}>
          <div className={styles.label}>{label}</div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onCloseButtonClick}
            data-testid="SelectModal.CloseButton"
          >
            <Icon name="times"></Icon>
          </button>
        </header>
        <ul>{renderedItems}</ul>
      </div>
    </ModalWithBackdrop>
  );
}) as <T>(props: SelectModalProps<T>) => JSX.Element;
